import type { DocumentNode } from '@apollo/client'
import type { OnDataOptions } from '@apollo/client/react/types/types'
import { getOperationName } from '@apollo/client/utilities'
import { useSubscription as useSubscriptionCore } from '@apollo/client/react'

export type UseSubscriptionProps<T> = {
  query?: DocumentNode
  onData?: (options: OnDataOptions<T>) => unknown
  filter?: Record<string, any> | null | undefined
  options?: Record<string, any> | null | undefined
  cacheQuery?: DocumentNode
  entityName?: string
}

export const useSubscription = <T>({
  query,
  filter,
  options,
  cacheQuery,
  onData,
  entityName,
}: UseSubscriptionProps<T>) => {
  const onDataDefault = ({ client, data }: OnDataOptions<T>) => {
    const subscriptionName = getOperationName(query)

    const newData = data.data[subscriptionName]

    // Step 1: Read existing data from cache
    const readQueryResult = client.readQuery({
      query: cacheQuery,
      variables: { filter }, // Ensure you're using the correct variables
    })

    const existingData = readQueryResult[entityName] as T

    if (!existingData) {
      return console.debug('No existing data found for subscription update.')
    }

    // Step 2: Merge new message into the existing list
    const updateData = {
      ...existingData,
      ...newData,
    }

    // Step 3: Write the merged data back to the cache
    client.writeQuery({
      query: cacheQuery,
      variables: { filter },
      data: {
        [entityName]: updateData,
      },
    })
  }

  onData ??= onDataDefault

  return useSubscriptionCore<T>(query, {
    variables: { filter, options },
    onData,
    shouldResubscribe: true,
  })
}
